// @flow
import React, { useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import type { ContextRouter, RouterHistory } from 'react-router-dom';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import { compose } from 'redux';

import CartContext from 'context/CartContext';
import withPageTracking from '../HOC/withPageTracking/withPageTracking';
import { getQueryParams } from '../utils/url';
import ScrollToTop from '../shared-components/ScrollToTop/ScrollToTop';
import PrivateRoute from './PrivateRoute';
import App from '../App';

import CheckoutPage from '../Scenes/Checkout/CheckoutPage';
import OrderConfirmationPage from '../Scenes/OrderConfirmation/OrderConfirmationPage';
import BoostPage from '../Scenes/Boost/BoostPage/BoostPage';
import NotFoundPage from '../Scenes/ErrorPages/NotFound/NotFoundPage';
import ProductDetailPage from '../Scenes/ProductDetail/ProductDetailPage';
import AdminMainPage from '../Scenes/Admin/MainPage';
import ExclusiveOffers from '../Scenes/ExclusiveOffers/ExclusiveOffers';
import RedeemInvitePage from '../Scenes/RedeemInvite/RedeemInvitePage';
import Subscriptions from 'Scenes/Subscriptions/Subscriptions';
import BoostUpsell from 'Scenes/BoostUpsell/BoostUpsell';

export const VersionContext = React.createContext<string>('add-ons');
export const selectedCategoryIdContext = React.createContext<string | null>(
  null
);
export const SearchedQueryContext = React.createContext<string | null>(null);
export const SelectedProductLinkContext = React.createContext<string | null>(
  null
);
export const AccordionInGridContext = React.createContext<boolean>(false);
export const HistoryContext = React.createContext<RouterHistory | void>();

function RouteProvider(props) {
  const { flags } = props;

  // redirect user to a LaunchDarkly configured path if the flag is on
  useEffect(() => {
    const isShopPath = _.includes(window.location.pathname, 'shop');
    const isShopRedirectFlagOn = flags.shopRedirect;
    const isAdminPath = _.includes(window.location.pathname, 'admin');
    const isAdminRedirectFlagOn = flags.adminRedirect;

    if (isShopRedirectFlagOn && isShopPath) {
      window.location = flags.shopRedirect;
    }

    if (isAdminRedirectFlagOn && isAdminPath) {
      window.location = flags.adminRedirect;
    }
  }, [flags]);

  return (
    <ScrollToTop>
      <Switch>
        <PrivateRoute path="/admin" component={AdminMainPage} />
        <Route
          path="/boost"
          render={(routeProps: ContextRouter) => {
            return (
              <ContextProvider {...routeProps}>
                <BoostPage />
              </ContextProvider>
            );
          }}
        />
        <Route
          path={['/add-ons', '/edit', '/shop']}
          exact
          render={(routeProps: ContextRouter) => {
            return (
              <ContextProvider {...routeProps}>
                <App />
              </ContextProvider>
            );
          }}
        />
        <Route
          path="/shop/checkout"
          exact
          render={(routeProps: ContextRouter) => {
            return (
              <ContextProvider {...routeProps}>
                <CheckoutPage />
              </ContextProvider>
            );
          }}
        />
        <Route
          path="/shop/order-confirmation"
          exact
          render={(routeProps: ContextRouter) => {
            return (
              <ContextProvider {...routeProps}>
                <OrderConfirmationPage />
              </ContextProvider>
            );
          }}
        />
        <Route
          path="/exclusive-offers"
          exact
          render={(routeProps: ContextRouter) => {
            return (
              <ContextProvider {...routeProps}>
                <ExclusiveOffers />
              </ContextProvider>
            );
          }}
        />
        <Route
          path="/product/:productSlug"
          exact
          render={(routeProps: ContextRouter) => {
            return (
              <ContextProvider {...routeProps}>
                <ProductDetailPage />
              </ContextProvider>
            );
          }}
        />
        <Route path="/redeem-invite">
          <CartContext>
            <RedeemInvitePage />
          </CartContext>
        </Route>
        <Route
          path="/boosts-q1"
          exact
          render={(routeProps: ContextRouter) => {
            return (
              <ContextProvider {...routeProps}>
                <Subscriptions />
              </ContextProvider>
            );
          }}
        />
        <Route
          path="/boosts-q2"
          exact
          render={(routeProps: ContextRouter) => {
            return (
              <ContextProvider {...routeProps}>
                <BoostUpsell />
              </ContextProvider>
            );
          }}
        />
        <Route
          render={(routeProps: ContextRouter) => {
            return (
              <ContextProvider {...routeProps}>
                <NotFoundPage />
              </ContextProvider>
            );
          }}
        />
      </Switch>
    </ScrollToTop>
  );
}

function ContextProvider(props: {| ...ContextRouter, children: React.Node |}) {
  const { history, location, children } = props;
  const { category, searchedQuery, product_link } = getQueryParams(
    location.search
  );
  let accordionInGrid = false;
  if (location.state) {
    accordionInGrid = location.state.accordionInGrid === true;
  }

  const getVersion = () => {
    if (location.pathname.includes('add-ons')) {
      return 'add-ons';
    }
    if (location.pathname.includes('shop')) {
      return 'shop';
    }
    if (location.pathname.includes('edit')) {
      return 'edit';
    }
    if (
      location.pathname.includes('boosts-q1') ||
      location.pathname.includes('boosts-q2')
    ) {
      return 'boosts';
    }
    return '';
  };

  const version = getVersion();

  return (
    <VersionContext.Provider value={version}>
      <selectedCategoryIdContext.Provider value={category}>
        <SearchedQueryContext.Provider value={searchedQuery}>
          <SelectedProductLinkContext.Provider value={product_link}>
            <AccordionInGridContext.Provider value={accordionInGrid}>
              <HistoryContext.Provider value={history}>
                <CartContext>{children}</CartContext>
              </HistoryContext.Provider>
            </AccordionInGridContext.Provider>
          </SelectedProductLinkContext.Provider>
        </SearchedQueryContext.Provider>
      </selectedCategoryIdContext.Provider>
    </VersionContext.Provider>
  );
}
export default compose(withPageTracking, withLDConsumer())(RouteProvider);
